/**
 * Builds the "Profit & Loss account cards with detailed transactions" model.
 *
 * The section/row order is intentionally identical to the Profit & Loss report of
 * LedgerReport (Components/LedgerReport/ProfitAndLossReportHtml): the backend returns
 * trial-balance rows already sorted by accounting group order -> category order ->
 * palcode order -> numeric account card id, and the report only groups that stream by
 * insertion order without ever re-sorting it. Subtotals mirror TableTotals.js so both
 * reports show the same numbers.
 */

import Constants from '../assests/Constants/constants';
import { groupBy } from './ReportUtils';

/** Amounts of the "הכנסות" group form the base of the "% of sales" column. */
const INCOME_GROUP = 'הכנסות';

export const formatPercentage = (value, totalIncome) => {
  if (!totalIncome || totalIncome <= 0) return '0.00%';
  return `${((value / totalIncome) * 100).toFixed(2)}%`;
};

/**
 * A palCodeAccountGroup belongs to Profit & Loss when at least one of its palcodes is
 * a result account (תוצאתי). Order of first appearance is the report order.
 */
export const collectProfitAndLossGroups = (rows) => {
  const groups = [];
  (rows || []).forEach((row) => {
    if (row.palCodeType === Constants.ResultAccountName && !groups.includes(row.palCodeAccountGroup)) {
      groups.push(row.palCodeAccountGroup);
    }
  });
  return groups;
};

const hasActivity = (row) => (
  row.initDebitAccount !== 0
  || row.initCreditAccount !== 0
  || row.debitAccount !== 0
  || row.creditAccount !== 0
  || row.balance !== 0
);

const sumBalance = (rows) => (rows || []).reduce((total, row) => total + (Number(row.balance) || 0), 0);

/**
 * Detailed transactions come from accountCardPalcodeDetails, which reports raw
 * debit/credit without the profit-and-loss sign flip that trialBalanceDetails applies.
 * Netting credit against debit puts the detail total in the same sign convention as the
 * balance shown on the account card row, so the two reconcile.
 */
export const sumTransactionsInProfitAndLossSign = (transactions) => (transactions || []).reduce(
  (total, transaction) => total + ((Number(transaction.creditAccount) || 0) - (Number(transaction.debitAccount) || 0)),
  0
);

/**
 * Section heading shown above each table, mirroring the label logic of the P&L report:
 * the classification is the main title and the "group - category" pair is the subtitle,
 * de-duplicated when the category name already starts with the group name.
 */
const buildSectionLabels = (agClassification, palCodeCategory, palCodeAccountGroup, t) => {
  const agLabel = agClassification === 'NonAccountingGroupClassifaction'
    ? t('NonAccountingGroupClassifaction')
    : agClassification;
  const categoryLabel = palCodeCategory === 'NonPalCodeCategory' ? t('NonPalCodeCategory') : palCodeCategory;
  const hasGroupLabel = palCodeAccountGroup && palCodeAccountGroup !== 'NonPalCodeAccountGroup';
  const groupLabel = hasGroupLabel ? palCodeAccountGroup : t('NonPalCodeAccountGroup');

  let detailLabel = agLabel;
  if (hasGroupLabel) {
    if (categoryLabel.startsWith(groupLabel)) {
      const categorySuffix = categoryLabel.slice(groupLabel.length).replace(/^[\s\-–—]+/, '');
      detailLabel = categorySuffix ? `${groupLabel} - ${categorySuffix}` : groupLabel;
    } else {
      detailLabel = `${groupLabel} - ${categoryLabel}`;
    }
  }

  return { agLabel, categoryLabel, groupLabel, detailLabel };
};

/**
 * @param {object}  params
 * @param {object}  params.trialBalanceData  response of trialBalanceDetails with isProfitAndLoss
 * @param {Map}     params.transactionsByCard  account card id -> { transactions, openingBalance }
 * @param {boolean} params.showZeros  keep account cards without any activity
 * @param {Function} params.t  i18next translate
 */
export const buildProfitAndLossCardsModel = ({
  trialBalanceData,
  transactionsByCard = new Map(),
  showZeros = false,
  t,
}) => {
  const allRows = trialBalanceData?.transaction || [];
  const profitAndLossGroups = collectProfitAndLossGroups(allRows);

  const rows = allRows
    .filter((row) => profitAndLossGroups.includes(row.palCodeAccountGroup))
    .filter((row) => showZeros || hasActivity(row));

  const totalIncome = rows.reduce(
    (total, row) => (row.palCodeAccountGroup === INCOME_GROUP && row.balance > 0 ? total + row.balance : total),
    0
  );

  const sections = [];
  let runningTotal = 0;
  let transactionsCount = 0;

  const byClassification = groupBy(rows, 'accountingGroupClassification');

  Object.keys(byClassification).forEach((agClassification) => {
    const classificationRows = byClassification[agClassification];
    const byCategory = groupBy(classificationRows, 'palCodeCategory');
    const categoryKeys = Object.keys(byCategory);

    categoryKeys.forEach((categoryKey, categoryIndex) => {
      const categoryRows = byCategory[categoryKey];
      const accountingGroupName = categoryRows[0]?.palCodeAccountGroup
        || classificationRows[0]?.palCodeAccountGroup
        || '';
      const isLastCategoryInGroup = categoryIndex === categoryKeys.length - 1;

      const byPalCode = groupBy(categoryRows, 'palCode');
      const palCodes = Object.keys(byPalCode).map((palCode) => {
        const palCodeRows = byPalCode[palCode];

        const cards = palCodeRows.map((row) => {
          const detail = transactionsByCard.get(String(row.accountCardId));
          const transactions = detail?.transactions || [];
          transactionsCount += transactions.length;

          return {
            key: `${row.accountCardId}_${row.palCode}_${categoryKey}`,
            accountCardId: row.accountCardId,
            accountCardName: row.accountCardName,
            palCode: row.palCode,
            balance: row.balance,
            percentage: formatPercentage(row.balance, totalIncome),
            transactions,
            transactionsTotal: sumTransactionsInProfitAndLossSign(transactions),
            openingBalance: detail?.openingBalance ?? 0,
          };
        });

        const total = sumBalance(palCodeRows);
        runningTotal += total;

        return {
          key: `${agClassification}_${categoryKey}_${palCode}`,
          palCode,
          palCodeLabel: palCode === 'NonPalCode' ? t('NonPalCode') : palCode,
          total,
          percentage: formatPercentage(total, totalIncome),
          cards,
        };
      });

      const categoryTotal = sumBalance(categoryRows);
      const { agLabel, categoryLabel, detailLabel } = buildSectionLabels(
        agClassification,
        categoryKey,
        accountingGroupName,
        t
      );

      // The group subtotal is taken from the unfiltered rows so hiding zero-balance cards
      // never changes the group total, exactly as the P&L report does.
      const groupTotal = isLastCategoryInGroup
        ? sumBalance(allRows.filter((row) => row.palCodeAccountGroup === accountingGroupName))
        : 0;

      sections.push({
        key: `${agClassification}_${categoryKey}`,
        accountingGroupClassification: agClassification,
        accountingGroupName,
        palCodeCategory: categoryKey,
        agLabel,
        categoryLabel,
        detailLabel,
        showAgLabel: categoryIndex === 0,
        palCodes,
        categoryTotal,
        categoryPercentage: formatPercentage(categoryTotal, totalIncome),
        isLastCategoryInGroup,
        groupTotal,
        groupPercentage: formatPercentage(groupTotal, totalIncome),
        // The P&L report leaves the running row empty for the income group, since at that
        // point it would only repeat the group subtotal.
        showRunningTotal: isLastCategoryInGroup && accountingGroupName !== INCOME_GROUP,
        runningTotal,
        runningPercentage: formatPercentage(runningTotal, totalIncome),
      });
    });
  });

  return {
    sections,
    totalIncome,
    grandTotal: runningTotal,
    grandTotalPercentage: formatPercentage(runningTotal, totalIncome),
    cardsCount: rows.length,
    transactionsCount,
  };
};

/** account card id -> detailed transactions, from an accountCardPalcodeDetails response. */
export const buildTransactionsByCard = (accountCardPalcodeResponse) => {
  const map = new Map();
  (accountCardPalcodeResponse?.accountCards || []).forEach((card) => {
    const cardId = String(card.accountCardId ?? '');
    if (!cardId) return;

    const existing = map.get(cardId);
    if (existing) {
      existing.transactions = existing.transactions.concat(card.transactions || []);
      return;
    }

    map.set(cardId, {
      accountCardId: card.accountCardId,
      accountCardName: card.accountCardName,
      palCode: card.palCode,
      openingBalance: Number(card.balance) || 0,
      transactions: [...(card.transactions || [])],
    });
  });
  return map;
};
